import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaStar } from 'react-icons/fa';
import {
  FiCheck,
  FiBell,
  FiClock,
  FiCornerUpLeft,
  FiExternalLink,
  FiFilter,
  FiMail,
  FiMessageSquare,
  FiPhone,
  FiRefreshCw,
  FiSearch,
  FiSend,
  FiTrash2,
  FiUser,
  FiX,
} from 'react-icons/fi';
import { api } from '../services/api';
import { resolveSettingMediaUrl } from '../context/SiteSettingsContext';
import './AdminProductReviews.css';

const STATUSES = {
  pending: { label: 'در انتظار بررسی', tone: 'amber', icon: FiClock },
  approved: { label: 'تأییدشده', tone: 'green', icon: FiCheck },
  rejected: { label: 'ردشده', tone: 'red', icon: FiX },
};

const formatDate = (value) => (
  value
    ? new Date(value).toLocaleString('fa-IR', { dateStyle: 'medium', timeStyle: 'short' })
    : '—'
);

const AdminRating = ({ value }) => (
  <span className="admin-review-rating" aria-label={`امتیاز ${value} از ۵`}>
    {[1, 2, 3, 4, 5].map((rating) => <FaStar key={rating} className={rating <= Number(value) ? 'is-active' : ''} />)}
    <b>{Number(value || 0).toLocaleString('fa-IR')}</b>
  </span>
);

const AdminProductReviews = ({ products = [], onCountChange }) => {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, pages: 1, counts: { all: 0, pending: 0, approved: 0, rejected: 0 } });
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState({ search: '', status: 'pending', rating: '', product: '' });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [replyBody, setReplyBody] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [removingReply, setRemovingReply] = useState(false);
  const [notice, setNotice] = useState(null);
  const directReviewLoaded = useRef(false);

  const fetchReviews = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const query = new URLSearchParams({ page: String(page), limit: '20' });
      Object.entries(filters).forEach(([key, value]) => {
        if (String(value || '').trim()) query.set(key, String(value).trim());
      });
      const response = await api.get(`/api/product-reviews/admin/list?${query.toString()}`);
      const data = response.data || {};
      const counts = data.counts || { all: 0, pending: 0, approved: 0, rejected: 0 };
      setItems(Array.isArray(data.items) ? data.items : []);
      setMeta({ total: data.total || 0, page: data.page || 1, pages: data.pages || 1, counts });
      onCountChange?.(Number(counts.pending || 0));
    } catch (error) {
      setNotice({ type: 'error', text: error.data?.message || error.message || 'دریافت نظرات انجام نشد.' });
    } finally {
      setLoading(false);
    }
  }, [filters, onCountChange, page]);

  useEffect(() => {
    const timer = setTimeout(() => fetchReviews(), filters.search ? 350 : 0);
    return () => clearTimeout(timer);
  }, [fetchReviews, filters.search]);

  useEffect(() => {
    if (directReviewLoaded.current) return;
    directReviewLoaded.current = true;
    const reviewId = new URLSearchParams(window.location.search).get('review');
    if (!reviewId) return;
    api.get(`/api/product-reviews/admin/${reviewId}`)
      .then((response) => {
        if (response.data) {
          setSelected(response.data);
          setRejectionReason(response.data.rejectionReason || '');
          setReplyBody(response.data.reply?.body || '');
        }
      })
      .catch(() => {});
  }, []);

  const openReview = (review) => {
    setSelected(review);
    setRejectionReason(review.rejectionReason || '');
    setReplyBody(review.reply?.body || '');
    setNotice(null);
  };

  const updateStatus = async (status) => {
    if (!selected || saving) return;
    setSaving(status);
    setNotice(null);
    try {
      const response = await api.put(`/api/product-reviews/admin/${selected._id}`, {
        status,
        rejectionReason: status === 'rejected' ? rejectionReason : '',
      });
      setSelected(response.data);
      setRejectionReason(response.data.rejectionReason || '');
      setReplyBody(response.data.reply?.body || '');
      setItems((current) => current.map((item) => item._id === response.data._id ? response.data : item));
      setNotice({ type: 'success', text: response.message || 'وضعیت نظر ذخیره شد.' });
      await fetchReviews({ silent: true });
    } catch (error) {
      setNotice({ type: 'error', text: error.data?.message || error.message || 'تغییر وضعیت نظر انجام نشد.' });
    } finally {
      setSaving('');
    }
  };

  const saveReply = async () => {
    const normalizedReply = replyBody.trim();
    if (!selected || sendingReply || removingReply || normalizedReply.length < 3) return;
    setSendingReply(true);
    setNotice(null);
    try {
      const response = await api.put(`/api/product-reviews/admin/${selected._id}/reply`, {
        body: normalizedReply,
      });
      setSelected(response.data);
      setReplyBody(response.data.reply?.body || '');
      setRejectionReason(response.data.rejectionReason || '');
      setItems((current) => current.map((item) => item._id === response.data._id ? response.data : item));
      setNotice({ type: 'success', text: response.message || 'پاسخ مدیر منتشر شد.' });
      await fetchReviews({ silent: true });
    } catch (error) {
      setNotice({ type: 'error', text: error.data?.message || error.message || 'ثبت پاسخ انجام نشد.' });
    } finally {
      setSendingReply(false);
    }
  };

  const deleteReply = async () => {
    if (!selected?.reply?.body || removingReply || sendingReply || !window.confirm('پاسخ رسمی هریاد از زیر این نظر حذف شود؟')) return;
    setRemovingReply(true);
    setNotice(null);
    try {
      const response = await api.delete(`/api/product-reviews/admin/${selected._id}/reply`);
      setSelected(response.data);
      setReplyBody('');
      setItems((current) => current.map((item) => item._id === response.data._id ? response.data : item));
      setNotice({ type: 'success', text: response.message || 'پاسخ مدیر حذف شد.' });
      await fetchReviews({ silent: true });
    } catch (error) {
      setNotice({ type: 'error', text: error.data?.message || error.message || 'حذف پاسخ انجام نشد.' });
    } finally {
      setRemovingReply(false);
    }
  };

  const deleteReview = async () => {
    if (!selected || deleting || !window.confirm(`نظر «${selected.authorName}» برای ${selected.product?.name || 'این محصول'} برای همیشه حذف شود؟`)) return;
    setDeleting(true);
    setNotice(null);
    try {
      const response = await api.delete(`/api/product-reviews/admin/${selected._id}`);
      setSelected(null);
      setRejectionReason('');
      setReplyBody('');
      setNotice({ type: 'success', text: response.message || 'نظر حذف شد.' });
      if (items.length === 1 && page > 1) setPage((value) => value - 1);
      else await fetchReviews({ silent: true });
    } catch (error) {
      setNotice({ type: 'error', text: error.data?.message || error.message || 'حذف نظر انجام نشد.' });
    } finally {
      setDeleting(false);
    }
  };

  const summaryCards = [
    { key: 'all', label: 'همه نظرات', icon: FiMessageSquare, tone: 'blue' },
    { key: 'pending', label: 'در انتظار', icon: FiClock, tone: 'amber' },
    { key: 'approved', label: 'تأییدشده', icon: FiCheck, tone: 'green' },
    { key: 'rejected', label: 'ردشده', icon: FiX, tone: 'red' },
  ];
  const selectedStatus = selected ? STATUSES[selected.status] || STATUSES.pending : null;
  const SelectedStatusIcon = selectedStatus?.icon;

  return (
    <div className="admin-product-reviews" dir="rtl">
      <div className="admin-review-summary">
        {summaryCards.map(({ key, label, icon: Icon, tone }) => (
          <button
            key={key}
            type="button"
            className={`is-${tone} ${filters.status === (key === 'all' ? '' : key) ? 'is-active' : ''}`}
            onClick={() => { setFilters((current) => ({ ...current, status: key === 'all' ? '' : key })); setPage(1); }}
          >
            <span><Icon /></span>
            <div><small>{label}</small><strong>{Number(meta.counts[key] || 0).toLocaleString('fa-IR')}</strong></div>
          </button>
        ))}
      </div>

      <div className="admin-review-toolbar">
        <label className="admin-review-search"><FiSearch /><input value={filters.search} onChange={(event) => { setFilters((current) => ({ ...current, search: event.target.value })); setPage(1); }} placeholder="جستجو در نام کاربر، محصول یا متن نظر..." /></label>
        <label><FiFilter /><select value={filters.rating} onChange={(event) => { setFilters((current) => ({ ...current, rating: event.target.value })); setPage(1); }}><option value="">همه امتیازها</option>{[5, 4, 3, 2, 1].map((rating) => <option key={rating} value={rating}>{rating.toLocaleString('fa-IR')} ستاره</option>)}</select></label>
        <label><FiMessageSquare /><select value={filters.product} onChange={(event) => { setFilters((current) => ({ ...current, product: event.target.value })); setPage(1); }}><option value="">همه محصولات</option>{products.map((product) => <option key={product._id || product.id} value={product._id || product.id}>{product.name}</option>)}</select></label>
        <button type="button" onClick={() => fetchReviews()} aria-label="به‌روزرسانی نظرات"><FiRefreshCw /></button>
      </div>

      {notice?.text && <div className={`admin-review-notice is-${notice.type}`} role={notice.type === 'error' ? 'alert' : 'status'}>{notice.text}</div>}

      <div className="admin-review-workspace">
        <section className="admin-review-list" aria-label="فهرست نظرات">
          {loading ? (
            <div className="admin-review-empty">در حال دریافت نظرات...</div>
          ) : items.length === 0 ? (
            <div className="admin-review-empty"><FiMessageSquare /><strong>نظری با این فیلتر پیدا نشد</strong><span>فیلترها را تغییر دهید یا فهرست را به‌روز کنید.</span></div>
          ) : items.map((review) => {
            const status = STATUSES[review.status] || STATUSES.pending;
            return (
              <button key={review._id} type="button" className={selected?._id === review._id ? 'is-active' : ''} onClick={() => openReview(review)}>
                <span className="admin-review-list-avatar">
                  {review.user?.avatar ? <img src={resolveSettingMediaUrl(review.user.avatar)} alt="" /> : <FiUser />}
                </span>
                <span className="admin-review-list-copy">
                  <span><strong>{review.authorName}</strong><em className={`is-${status.tone}`}>{status.label}</em></span>
                  <small>{review.product?.name || 'محصول حذف‌شده'}</small>
                  <p>{review.body}</p>
                  <span><AdminRating value={review.rating} /><time>{formatDate(review.createdAt)}</time></span>
                </span>
              </button>
            );
          })}

          {meta.pages > 1 && (
            <div className="admin-review-pagination">
              <button type="button" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>قبلی</button>
              <span>صفحه {page.toLocaleString('fa-IR')} از {meta.pages.toLocaleString('fa-IR')}</span>
              <button type="button" disabled={page >= meta.pages} onClick={() => setPage((value) => value + 1)}>بعدی</button>
            </div>
          )}
        </section>

        <section className="admin-review-detail" aria-label="مدیریت نظر انتخاب‌شده">
          {!selected ? (
            <div className="admin-review-empty"><FiMessageSquare /><strong>یک نظر را انتخاب کنید</strong><span>جزئیات کاربر و ابزارهای تأیید در اینجا نمایش داده می‌شوند.</span></div>
          ) : (
            <>
              <header className="admin-review-detail-head">
                <div>
                  <span>نظر محصول</span>
                  <h2>{selected.product?.name || 'محصول حذف‌شده'}</h2>
                  <time>ثبت‌شده در {formatDate(selected.createdAt)}</time>
                </div>
                <em className={`admin-review-status is-${selectedStatus.tone}`}><SelectedStatusIcon /> {selectedStatus.label}</em>
              </header>

              <div className="admin-review-user-card">
                <span>{selected.user?.avatar ? <img src={resolveSettingMediaUrl(selected.user.avatar)} alt="" /> : <FiUser />}</span>
                <div><strong>{selected.user?.name || selected.authorName}</strong><small>نویسنده نظر</small></div>
                {selected.user?.email && <a href={`mailto:${selected.user.email}`}><FiMail /> {selected.user.email}</a>}
                {selected.user?.phone && <a href={`tel:${selected.user.phone.replace(/[^+\d]/g, '')}`}><FiPhone /> {selected.user.phone}</a>}
              </div>

              <article className="admin-review-body">
                <header><span>متن نظر کاربر</span><AdminRating value={selected.rating} /></header>
                <p>{selected.body}</p>
              </article>

              <section className={`admin-review-reply-editor ${selected.reply?.body ? 'has-reply' : ''}`}>
                <header>
                  <div>
                    <span><FiCornerUpLeft /></span>
                    <div>
                      <strong>پاسخ رسمی هریاد</strong>
                      <small>پاسخ زیر همین نظر در صفحه محصول نمایش داده می‌شود.</small>
                    </div>
                  </div>
                  {selected.reply?.body && (
                    <em>منتشرشده در {formatDate(selected.reply.updatedAt || selected.reply.createdAt)}</em>
                  )}
                </header>
                <textarea
                  value={replyBody}
                  onChange={(event) => setReplyBody(event.target.value.slice(0, 2000))}
                  placeholder="پاسخ روشن، محترمانه و کاربردی هریاد را برای این کاربر بنویسید..."
                  rows="5"
                  maxLength="2000"
                  disabled={sendingReply || removingReply}
                />
                <footer>
                  <div>
                    <span><FiBell /></span>
                    <p>
                      <strong>اعلان خودکار به کاربر</strong>
                      <small>پس از ثبت یا تغییر پاسخ، ایمیل و پیامک مطابق تنظیمات اعلان‌ها ارسال می‌شود.</small>
                    </p>
                  </div>
                  <span className="admin-review-reply-count">{replyBody.length.toLocaleString('fa-IR')} / ۲۰۰۰</span>
                  {selected.reply?.body && (
                    <button type="button" className="is-remove" disabled={sendingReply || removingReply} onClick={deleteReply}>
                      <FiTrash2 /> {removingReply ? 'در حال حذف...' : 'حذف پاسخ'}
                    </button>
                  )}
                  <button type="button" className="is-send" disabled={sendingReply || removingReply || replyBody.trim().length < 3} onClick={saveReply}>
                    <FiSend /> {sendingReply ? 'در حال ارسال...' : selected.reply?.body ? 'ذخیره و ارسال اعلان' : 'انتشار پاسخ'}
                  </button>
                </footer>
              </section>

              <label className="admin-review-reason">
                <span>یادداشت رد نظر <small>در حساب کاربر نمایش داده می‌شود</small></span>
                <textarea value={rejectionReason} onChange={(event) => setRejectionReason(event.target.value.slice(0, 500))} placeholder="مثلاً: لطفاً اطلاعات شخصی یا لینک تبلیغاتی را از متن حذف کنید." rows="3" />
              </label>

              <div className="admin-review-actions">
                <button type="button" className="is-approve" disabled={Boolean(saving) || deleting} onClick={() => updateStatus('approved')}><FiCheck /> {saving === 'approved' ? 'در حال ذخیره...' : 'تأیید و انتشار'}</button>
                <button type="button" className="is-reject" disabled={Boolean(saving) || deleting} onClick={() => updateStatus('rejected')}><FiX /> {saving === 'rejected' ? 'در حال ذخیره...' : 'رد نظر'}</button>
                <button type="button" className="is-pending" disabled={Boolean(saving) || deleting} onClick={() => updateStatus('pending')}><FiClock /> بازگرداندن به انتظار</button>
                {selected.product?.slug && <a href={`/products/${selected.product.slug}`} target="_blank" rel="noreferrer"><FiExternalLink /> مشاهده محصول</a>}
                <button type="button" className="is-delete" disabled={Boolean(saving) || deleting} onClick={deleteReview}><FiTrash2 /> {deleting ? 'در حال حذف...' : 'حذف دائمی'}</button>
              </div>

              {selected.reviewedAt && (
                <p className="admin-review-audit">آخرین بررسی در {formatDate(selected.reviewedAt)}{selected.reviewedBy?.name ? ` توسط ${selected.reviewedBy.name}` : ''}</p>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default AdminProductReviews;
